//prime react
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { MultiSelect } from "primereact/multiselect"

//formik
import { useFormik } from "formik"

//styles
import styles from "../../../../styles/kiosk/KioskActivityCheckInOptions.module.css"
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"

//helpers
import { getFormErrorMessage, isFormFieldValid } from "../helpers/helpers"
import { LogError } from "../../../../helpers/logger"

//api
import {
  kioskApiPostDeleteActivityCheckInOption,
  kioskApiPostSaveActivityCheckInOption,
} from "../../kioskApi"

//context
import { useContext, useEffect } from "react"
import { UserContext } from "../../../../context/userContext"
import CustomTooltip from "../Utils/CustomTooltip"

const ActivityCheckInOptionModal = ({
  deleteOption,
  editOption,
  kiosk_organization_id,
  parentOptions,
  criteria,
  successMessages,
  failureMessages,
  onSave,
  onCancel,
}) => {
  const userCtx = useContext(UserContext)

  useEffect(() => {
    if (editOption) {
      formik.setFieldValue("id", editOption.id, false)
      formik.setFieldValue("name", editOption.name, false)
      formik.setFieldValue("parentId", editOption.parentId, false)
      formik.setFieldValue("successMessage", editOption.successMessage, false)
      formik.setFieldValue("failureMessage", editOption.failureMessage, false)
      formik.setFieldValue(
        "criterias",
        editOption?.criteria?.map((c) => c.id),
        false
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOption])

  const saveData = async (data) => {
    try {
      const payload = { ...data, organizationId: kiosk_organization_id }
      const result = await kioskApiPostSaveActivityCheckInOption(
        userCtx.tokenKiosk,
        payload
      )
      onSave()
      return result
    } catch (error) {
      if (error.response) {
        userCtx.setNotificationError(error?.response?.data)
        LogError(error?.response?.data, error)
      } else {
        LogError("Unable to save Activity Check In Options values", error)
        userCtx.setNotificationError(
          "Unable to save Activity Check In Options values"
        )
      }
      formik.setSubmitting(false)
    }
  }

  const deleteData = async (data) => {
    try {
      const payload = { ActivityOptionId: data.id }
      const result = await kioskApiPostDeleteActivityCheckInOption(
        userCtx.tokenKiosk,
        payload
      )
      onSave()
      return result
    } catch (error) {
      onCancel()
      LogError("Unable to delete Activity Check In value", error)
      userCtx.setNotificationError("Unable to delete Activity Check In Option")
    }
  }

  const formik = useFormik({
    initialValues: {
      id: undefined,
      name: "",
      parentId: undefined,
      successMessage: "",
      failureMessage: "",
      criterias: [],
    },
    validate: (data) => {
      let errors = {}

      if (!deleteOption) {
        if (!data?.name) {
          errors.name = "Name is required."
        }
        if (data.criterias.length > 0 && !data.successMessage) {
          errors.successMessage = "Success message is required."
        }
        if (data.criterias.length > 0 && !data.failureMessage) {
          errors.failureMessage = "Failure message is required."
        }
      }
      return errors
    },
    onSubmit: (data) => {
      if (deleteOption) {
        deleteData(data)
      } else {
        saveData(data)
      }
    },
  })

  const headerText = () => {
    if (deleteOption) {
      return "Are you sure you want to delete this item?"
    }
    return `${editOption ? "Edit" : "Add"} Option`
  }

  return (
    <Dialog visible={true} className={kioskStyles.addModal} showHeader={false}>
      <div className={kioskStyles.modalHeader}>
        {headerText()}
        <Button
          icon="pi pi-times"
          className={kioskStyles.hideModalButton}
          onClick={() => {
            onCancel()
          }}
          disabled={formik.isSubmitting}
        />
      </div>

      <div className={kioskStyles.modalFormContainer}>
        <hr className={kioskStyles.modalHeaderLine} />
        <form onSubmit={formik.handleSubmit}>
          <div className={kioskStyles.modalField}>
            <CustomTooltip
              label={"The name for this option in Kiosk"}
              position={"right"}
              targetId={"form_checkIn_name"}
            />
            <label className={kioskStyles.modalFieldLabel}>
              Name *
              <i
                className={`pi pi-info-circle ${kioskStyles.info}`}
                id="form_checkIn_name"
              ></i>
            </label>
            <InputText
              id="name"
              name="name"
              value={formik.values.name}
              onChange={(e) => {
                formik.handleChange(e)
              }}
              className={`${kioskStyles.modalFieldInput} ${
                isFormFieldValid(formik, "name") && kioskStyles.inputError
              }`}
              disabled={deleteOption || formik.isSubmitting}
            />
            {getFormErrorMessage(formik, "name")}
          </div>
          <div className={kioskStyles.modalField}>
            <CustomTooltip
              label={"The parent for this option in Kiosk"}
              position={"right"}
              targetId={"form_checkIn_parent"}
            />
            <label className={kioskStyles.modalFieldLabel}>
              Parent Option
              <i
                className={`pi pi-info-circle ${kioskStyles.info}`}
                id="form_checkIn_parent"
              ></i>
            </label>
            <Dropdown
              id="parentId"
              name="parentId"
              value={formik.values.parentId}
              onChange={(e) => {
                formik.handleChange(e)
              }}
              options={parentOptions?.filter(
                (p) =>
                  p.id !== editOption?.id &&
                  !editOption?.children?.some((child) => child.id === p.id)
              )}
              optionLabel="name"
              optionValue="id"
              className={`${kioskStyles.modalFieldInput} ${styles.modalDropDownInput}`}
              disabled={deleteOption || formik.isSubmitting}
              showClear={!!formik.values.parentId}
            />
          </div>
          <div className={kioskStyles.modalField}>
            <CustomTooltip
              label={
                "Select the participant statuses that are eligible to check-in to this activity."
              }
              position={"right"}
              targetId={"form_checkIn_criteria"}
            />
            <label className={kioskStyles.modalFieldLabel}>
              Participant Status Eligibility
              <i
                className={`pi pi-info-circle ${kioskStyles.info}`}
                id="form_checkIn_criteria"
              ></i>
            </label>
            <MultiSelect
              id="criterias"
              name="criterias"
              value={formik.values.criterias}
              onChange={(e) => {
                formik.handleChange(e)
              }}
              options={criteria}
              optionLabel="description"
              optionValue="id"
              panelClassName={styles.inputError}
              className={`${kioskStyles.modalFieldInput} ${kioskStyles.DropDownInput} ${styles.criteriaChips}`}
              disabled={deleteOption || formik.isSubmitting}
              maxSelectedLabels={5}
              display="chip"
              showClear={formik.values?.criterias?.length > 0}
            />
          </div>
          {formik.values.criterias.length > 0 && (
            <>
              <div className={kioskStyles.modalField}>
                <CustomTooltip
                  label={"The success message for this option in Kiosk"}
                  position={"right"}
                  targetId={"form_checkIn_success"}
                />
                <label className={kioskStyles.modalFieldLabel}>
                  Success Message *
                  <i
                    className={`pi pi-info-circle ${kioskStyles.info}`}
                    id="form_checkIn_success"
                  ></i>
                </label>
                <Dropdown
                  id="successMessage"
                  name="successMessage"
                  value={formik.values.successMessage}
                  onChange={(e) => {
                    if (e.value === undefined) {
                      e.value = ""
                      e.target.value = ""
                    }

                    formik.handleChange(e)
                  }}
                  options={successMessages}
                  className={`${kioskStyles.modalFieldInput} ${
                    kioskStyles.DropDownInput
                  } ${
                    isFormFieldValid(formik, "successMessage") &&
                    kioskStyles.inputError
                  }`}
                  disabled={deleteOption || formik.isSubmitting}
                  editable
                  showClear={!!formik.values.successMessage}
                />
                {getFormErrorMessage(formik, "successMessage")}
              </div>
              <div className={kioskStyles.modalField}>
                <CustomTooltip
                  label={"The failure message for this option in Kiosk"}
                  position={"right"}
                  targetId={"form_checkIn_fail"}
                />
                <label className={kioskStyles.modalFieldLabel}>
                  Failure Message *
                  <i
                    className={`pi pi-info-circle ${kioskStyles.info}`}
                    id="form_checkIn_fail"
                  ></i>
                </label>
                <Dropdown
                  id="failureMessage"
                  name="failureMessage"
                  value={formik.values.failureMessage}
                  onChange={(e) => {
                    if (e.value === undefined) {
                      e.value = ""
                      e.target.value = ""
                    }

                    formik.handleChange(e)
                  }}
                  options={failureMessages}
                  className={`${kioskStyles.modalFieldInput} ${
                    kioskStyles.DropDownInput
                  } ${
                    isFormFieldValid(formik, "failureMessage") &&
                    kioskStyles.inputError
                  }`}
                  disabled={deleteOption || formik.isSubmitting}
                  editable
                  showClear={!!formik.values.failureMessage}
                />
                {getFormErrorMessage(formik, "failureMessage")}
              </div>
            </>
          )}

          <div className={kioskStyles.modalFooter}>
            <Button
              label="Cancel"
              className={`${kioskStyles.modalFooterButton} ${kioskStyles.modalCancelButton}`}
              type="button"
              onClick={() => {
                onCancel()
              }}
              disabled={formik.isSubmitting}
            />
            <Button
              icon={formik.isSubmitting && "pi pi-spin pi-spinner"}
              label={deleteOption ? "Delete" : "Save"}
              type="submit"
              disabled={formik.isSubmitting || (!formik.dirty && !deleteOption)}
              className={kioskStyles.modalFooterButton}
            />
          </div>
        </form>
      </div>
    </Dialog>
  )
}
export default ActivityCheckInOptionModal
